import { parseJSONMap } from "appworks/utils/json-utils";
import { slotModel } from "./slot-model";

export interface SymbolConfig {
    /** @deprecated Unique integer id, 0 indexed (defaults to order in game-config) */
    index?: number;
    /** Symbol ID (should match static symbol asset name) */
    id: string;
    /** A verbose name used for debugging */
    name?: string;
    /** A list of aliases which this symbol can also be known by */
    aliases?: string[];
    wild?: boolean;
    /** Whether to include wild symbols towards a bonus match count */
    includeWilds?: boolean;
    scatter?: boolean;
    ways?: boolean;
    /** bonusIds above 0 denote a bonus symbol, different values can be used to identify different types. */
    bonusId?: number;
    /** @deprecated Always z-sort this symbol above all others (deprecated, use zIndex) */
    alwaysOnTop?: boolean;
    /** Z-sort position of this symbol */
    zIndex?: number;
    /** Number of symbols that must be hit before anticipation starts */
    anticipation?: number;
    /** Minimum matches to payout (used for bonus land sounds mostly) */
    matchesMin?: number;
    /** Maximum matches possible (used to cancel anticipation when max has been hit) */
    matchesMax?: number;
    /**
     * Maximum bonus symbol matches per reel
     * Used for calculating anticipation etc on games where you can get more than 1 bonus symbol per reel
     * @default 1
     */
    possibleMatchesPerReel?: number;
    /** Reels this symbol appears on. Used to determine whether to bother anticipating or making land sounds */
    reels?: number[];
    /** If set, contains a list of rows which this symbol will pay on */
    validRows?: number[];
    /** The width of a wide symbol which takes up more than 1 reel */
    width?: number;
    /** The height of a stacked symbol which takes up more than 1 row */
    height?: number;
    /** Upon landing this symbol should automatically stick */
    autoStick?: boolean;
    payouts?: any;
    /**
     * For mixed payout wins, where any of the following symbols count towards the mixed payout
     * Must include 1 or more of these to count as a win
     */
    mixed?: string[];
    /** When a symbol multiplies the total win it's part of (i.e 2x wild) */
    multiplier?: number;
    /** Some transitions might not want to waste time animating a blank symbol for example */
    isBlank?: boolean;
    /** If true, slotworks will generate a blurred texture for this symbol on startup and add it to the texture cache */
    blur?: boolean;
    /** If blur is true, how much should the symbols blur by? */
    blurStrength?: number;
}

export class SymbolDefinition implements SymbolConfig {
    public index: number;
    public id: string;
    public name: string;
    public aliases: string[];
    public wild: boolean;
    public includeWilds: boolean;
    public scatter: boolean;
    public ways: boolean;
    public bonusId: number;
    public alwaysOnTop: boolean;
    public zIndex: number;
    public anticipation: number;
    public matchesMin: number;
    public matchesMax: number;
    public possibleMatchesPerReel: number;
    public reels: number[];
    public validRows: number[];
    public height: number;
    public width: number;
    public autoStick: boolean;
    public payouts: Map<string, number>;
    public multiplier: number;
    public isBlank: boolean;
    public mixed: string[];
    public blur: boolean;
    public blurStrength: number;

    constructor(symbolConfig: SymbolConfig) {
        this.index = symbolConfig.index;
        this.id = symbolConfig.id;
        this.name = symbolConfig.name || symbolConfig.id;
        this.aliases = symbolConfig.aliases || [];
        this.wild = symbolConfig.wild;
        this.scatter = symbolConfig.scatter;
        this.ways = symbolConfig.ways;
        this.includeWilds = symbolConfig.includeWilds;
        this.bonusId = symbolConfig.bonusId || 0;
        this.alwaysOnTop = symbolConfig.alwaysOnTop;
        this.zIndex = symbolConfig.zIndex;
        this.anticipation = symbolConfig.anticipation;
        this.matchesMin = symbolConfig.matchesMin;
        this.matchesMax = symbolConfig.matchesMax;
        this.possibleMatchesPerReel = symbolConfig.possibleMatchesPerReel || 1;
        this.reels = symbolConfig.reels;
        this.validRows = symbolConfig.validRows;
        this.height = symbolConfig.height || 1;
        this.width = symbolConfig.width || 1;
        this.autoStick = symbolConfig.autoStick;
        this.mixed = symbolConfig.mixed;
        this.multiplier = symbolConfig.multiplier;
        this.isBlank = symbolConfig.isBlank;
        this.blur = symbolConfig.blur ?? false;
        this.blurStrength = symbolConfig.blurStrength ?? 200;

        if (symbolConfig.payouts) {
            let isMultiRtp = false;
            for (const key of Object.keys(symbolConfig.payouts)) {
                if (key === "default" || key.includes("rtp_")) { isMultiRtp = true; }
            }

            const rtp = slotModel.read().regulations.rtp;

            // Load the correct set of payouts for the given rtp
            let payouts = symbolConfig.payouts[`rtp_${rtp}`];
            if (!payouts) { payouts = symbolConfig.payouts["default"]; }
            if (!payouts) { payouts = symbolConfig.payouts; }

            this.payouts = parseJSONMap<number>(payouts);
        }
    }

    public isBonusSymbol() {
        return this.bonusId > 0;
    }
}
